import { useEffect, useMemo, useRef, useState } from 'react';
import { heldCargoApi, clientsApi, tripsApi, type Client, type HeldCargoItem, type Trip } from '@/api/client';
import { useAsync } from '@/hooks';
import { Modal } from './Modal';
import { Select } from './Select';
import { Button } from './Button';
import { useToast } from './Feedback';
import { formatDate, humanize } from '@/lib/format';

function ageInDays(receivedAt: string): number {
  return Math.floor((Date.now() - new Date(receivedAt).getTime()) / 86_400_000);
}

const RELEASABLE_STATUSES = ['requested', 'confirmed', 'dispatched'];

interface HeldCargoPanelProps {
  /** Change this value (e.g. a counter bumped by the board's Refresh) to reload the panel. */
  refreshKey?: number;
  /** Called after cargo is added to a trip, so the parent can refresh its own data. */
  onReleased?: () => void;
}

export function HeldCargoPanel({ refreshKey, onReleased }: HeldCargoPanelProps = {}) {
  const toast = useToast();
  const { data: cargo, loading, reload } = useAsync(() => heldCargoApi.list(), []);
  const { data: agedCargo, reload: reloadAged } = useAsync(() => heldCargoApi.list(true), []);
  const { data: clients } = useAsync(() => clientsApi.list(), []);
  const { data: trips, reload: reloadTrips } = useAsync(() => tripsApi.list(), []);

  const firstRefresh = useRef(true);
  useEffect(() => {
    if (firstRefresh.current) {
      firstRefresh.current = false;
      return;
    }
    void reload();
    void reloadAged();
    void reloadTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const [releaseTarget, setReleaseTarget] = useState<HeldCargoItem | null>(null);
  const [releaseTripId, setReleaseTripId] = useState('');
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState('');
  const [expanded, setExpanded] = useState(true);

  const clientNameById = useMemo(
    () => Object.fromEntries((clients ?? []).map((c: Client) => [c.id, c.legalName])),
    [clients],
  );
  const agedIds = useMemo(() => new Set((agedCargo ?? []).map((c) => c.id)), [agedCargo]);

  const items = cargo ?? [];

  const candidateTrips = useMemo(() => {
    if (!releaseTarget) return [];
    return (trips ?? []).filter(
      (t: Trip) => t.clientId === releaseTarget.clientId && RELEASABLE_STATUSES.includes(t.status),
    );
  }, [trips, releaseTarget]);

  const openRelease = (item: HeldCargoItem) => {
    setReleaseTarget(item);
    setReleaseTripId('');
    setReleaseError('');
  };

  const handleRelease = async () => {
    if (!releaseTarget || !releaseTripId) return;
    setReleasing(true);
    setReleaseError('');
    try {
      await heldCargoApi.release(releaseTarget.id, releaseTripId);
      const trip = (trips ?? []).find((t) => t.id === releaseTripId);
      toast(`${releaseTarget.pieces} piece${releaseTarget.pieces === 1 ? '' : 's'} added to ${trip?.tripNumber ?? trip?.reference ?? 'the trip'}`);
      setReleaseTarget(null);
      void reload();
      void reloadAged();
      onReleased?.();
    } catch (err) {
      setReleaseError((err as { message?: string }).message ?? 'Failed to add held cargo to the trip');
    } finally {
      setReleasing(false);
    }
  };

  if (!loading && items.length === 0) return null;

  return (
    <div className="held-cargo-panel">
      <button type="button" className="held-cargo-header" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
        <span>
          Held cargo <span className="held-cargo-badge">{items.length}</span>
          {agedIds.size > 0 && <span className="held-cargo-badge held-cargo-badge-urgent">{agedIds.size} aged</span>}
        </span>
        <span className="held-cargo-toggle">{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div className="held-cargo-list">
          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : (
            items.map((item) => {
              const aged = agedIds.has(item.id);
              return (
                <div key={item.id} className={`held-cargo-row ${aged ? 'aged' : ''}`}>
                  <div className="held-cargo-info">
                    <div className="held-cargo-client">
                      {clientNameById[item.clientId] ?? 'Unknown client'}
                      {aged && <span className="held-cargo-age-badge">Aged</span>}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                      {item.pieces} piece{item.pieces === 1 ? '' : 's'} · {item.reason}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Received {ageInDays(item.receivedAt)}d ago
                    </div>
                  </div>
                  <Button type="button" size="sm" variant="secondary" onClick={() => openRelease(item)}>
                    Add to trip…
                  </Button>
                </div>
              );
            })
          )}
        </div>
      )}

      <Modal
        open={releaseTarget !== null}
        onClose={() => setReleaseTarget(null)}
        title="Add held cargo to a trip"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setReleaseTarget(null)}>Cancel</Button>
            <Button type="button" onClick={handleRelease} loading={releasing} disabled={!releaseTripId}>
              Add to trip
            </Button>
          </>
        }
      >
        {releaseTarget && (
          <>
            <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
              {releaseTarget.pieces} piece{releaseTarget.pieces === 1 ? '' : 's'} for{' '}
              {clientNameById[releaseTarget.clientId] ?? 'this client'}, held since {formatDate(releaseTarget.receivedAt)}
              {' — '}{releaseTarget.reason}. Choose an open trip for the same client to carry it.
            </p>
            {releaseError && <div className="form-error">{releaseError}</div>}
            <Select
              label="Trip"
              value={releaseTripId}
              onChange={(e) => setReleaseTripId(e.target.value)}
              required
            >
              <option value="">Select a trip for this client…</option>
              {candidateTrips.map((t: Trip) => (
                <option key={t.id} value={t.id}>
                  {t.tripNumber || t.reference || 'Trip'}
                  {t.scheduledDate ? ` · ${formatDate(t.scheduledDate)}` : ''} · {humanize(t.status)}
                </option>
              ))}
            </Select>
            {candidateTrips.length === 0 && (
              <p className="input-hint">
                No open trip found for this client. Book or confirm a trip for this client first, then add the cargo to it.
              </p>
            )}
          </>
        )}
      </Modal>

      <style>{`
        .held-cargo-panel {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          margin-bottom: var(--space-lg);
          overflow: hidden;
        }
        .held-cargo-header {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-md) var(--space-lg);
          background: none;
          border: none;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.9375rem;
        }
        .held-cargo-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 1.5rem;
          padding: 0 6px;
          margin-left: var(--space-sm);
          border-radius: 999px;
          background: var(--color-sand);
          font-size: 0.75rem;
          font-weight: 700;
        }
        .held-cargo-badge-urgent {
          background: var(--color-urgent);
          color: white;
        }
        .held-cargo-toggle { color: var(--color-text-muted); }
        .held-cargo-list {
          border-top: 1px solid var(--color-border);
          display: flex;
          flex-direction: column;
        }
        .held-cargo-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--space-md);
          padding: var(--space-md) var(--space-lg);
          border-bottom: 1px solid var(--color-border);
        }
        .held-cargo-row:last-child { border-bottom: none; }
        .held-cargo-row.aged { background: #FDF3E7; }
        .held-cargo-client { font-weight: 600; font-size: 0.9375rem; }
        .held-cargo-age-badge {
          margin-left: var(--space-sm);
          font-size: 0.6875rem;
          font-weight: 700;
          color: var(--color-warning);
          border: 1px solid var(--color-warning);
          border-radius: var(--radius-sm);
          padding: 1px 6px;
        }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-md);
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
}
